import React, { memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Lock, Eye, Database, UserCheck, ExternalLink } from 'lucide-react';
import { Z_CLASS } from '../../../constants/zIndex';
import { haptic } from '../../../shared/utils/haptics';

/**
 * PrivacyPolicy - Modal component with dark theme
 */
export const PrivacyPolicy = memo(function PrivacyPolicy({ isOpen = true, onClose }) {
  const handleClose = useCallback(() => {
    haptic.tap();
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className={`fixed inset-0 bg-black/80 backdrop-blur-sm ${Z_CLASS.PRIVACY_POLICY} flex items-center justify-center p-4`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="glass-elevated w-full max-w-lg max-h-[95svh] flex flex-col overflow-y-auto"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        >
          {/* Header */}
          <div className="p-4 border-b border-white/[0.06] flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 glass-card flex items-center justify-center">
                  <Shield className="w-5 h-5 text-zinc-500" />
                </div>
                <div>
                  <h2 className="text-base font-medium text-zinc-100">
                    Politique de Confidentialité
                  </h2>
                  <p className="text-[10px] text-zinc-500">
                    Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
              <motion.button
                onClick={handleClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <X className="w-4 h-4 text-zinc-400" />
              </motion.button>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-grow overflow-y-auto p-4 space-y-6">
            {/* Introduction */}
            <PolicySection
              icon={<Eye className="w-4 h-4" />}
              title="Introduction"
            >
              <p>
                Bienvenue sur ADHD Planner. Nous accordons une grande importance
                à la confidentialité de vos données. Cette politique explique
                comment nous collectons, utilisons et protégeons vos informations.
              </p>
            </PolicySection>

            {/* Data Collection */}
            <PolicySection
              icon={<Database className="w-4 h-4" />}
              title="Données collectées"
            >
              <p className="mb-3">L'Application collecte et traite les données suivantes :</p>
              <ul className="space-y-2">
                <PolicyListItem
                  title="Données locales"
                  description="Tâches, préférences et statuts stockés localement sur votre appareil via le Local Storage du navigateur."
                />
                <PolicyListItem
                  title="Données Google Calendar"
                  description="Si connecté, nous accédons à vos calendriers et événements pour les afficher dans votre planning."
                />
              </ul>
            </PolicySection>

            {/* Google API Usage */}
            <PolicySection
              icon={<Lock className="w-4 h-4" />}
              title="Utilisation des données Google"
            >
              <p className="mb-3">
                L'utilisation des informations reçues des API Google respecte la{' '}
                <a
                  href="https://developers.google.com/terms/api-services-user-data-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-300 hover:text-white underline underline-offset-2 inline-flex items-center gap-1"
                >
                  Politique relative aux données utilisateur
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>
              <ul className="space-y-2">
                <PolicyListItem
                  title="Lecture seule"
                  description="Nous utilisons l'accès en lecture seule pour importer vos événements."
                />
                <PolicyListItem
                  title="Affichage uniquement"
                  description="Ces données ne sont utilisées que pour l'affichage dans l'interface."
                />
                <PolicyListItem
                  title="Pas de transfert"
                  description="Aucune donnée Google Calendar n'est envoyée à des serveurs tiers."
                />
              </ul>
            </PolicySection>

            {/* Security */}
            <PolicySection
              icon={<Shield className="w-4 h-4" />}
              title="Stockage et Sécurité"
            >
              <p>
                L'Application fonctionne principalement côté client. Vos données
                personnelles ne transitent pas par nos serveurs, à l'exception
                des interactions directes entre votre navigateur et les services
                Google (authentification OAuth).
              </p>
            </PolicySection>

            {/* Rights */}
            <PolicySection
              icon={<UserCheck className="w-4 h-4" />}
              title="Vos droits"
            >
              <p className="mb-3">Vous pouvez à tout moment :</p>
              <ul className="space-y-2">
                <PolicyListItem
                  description="Déconnecter votre compte Google via les paramètres de l'Application."
                />
                <PolicyListItem
                  description="Supprimer les données locales en vidant le cache de votre navigateur."
                />
                <PolicyListItem
                  description={
                    <>
                      Révoquer l'accès via la page{' '}
                      <a
                        href="https://myaccount.google.com/permissions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-300 hover:text-white underline underline-offset-2"
                      >
                        Sécurité de votre compte Google
                      </a>
                    </>
                  }
                />
              </ul>
            </PolicySection>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/[0.06] flex-shrink-0">
            <motion.button
              onClick={handleClose}
              className="w-full py-2.5 bg-zinc-700 hover:bg-zinc-600 text-zinc-100 rounded-xl font-medium text-sm transition-colors"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              Fermer
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
});

// Section component
const PolicySection = memo(function PolicySection({ icon, title, children }) {
  return (
    <motion.section
      className="space-y-3"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center gap-2">
        <span className="text-zinc-500">{icon}</span>
        <h3 className="text-sm font-medium text-zinc-200">{title}</h3>
      </div>
      <div className="text-sm text-zinc-400 leading-relaxed pl-6">
        {children}
      </div>
    </motion.section>
  );
});

// List item component
const PolicyListItem = memo(function PolicyListItem({ title, description }) {
  return (
    <li className="flex items-start gap-2">
      <span className="w-1 h-1 rounded-full bg-zinc-600 mt-2 flex-shrink-0" />
      <span>
        {title && <strong className="text-zinc-300">{title} : </strong>}
        {description}
      </span>
    </li>
  );
});

export default PrivacyPolicy;
